// loyalty-onboarding.js

const LOYALTY_SELECTORS = {
  STEP_1: '[data-loyalty-step-1]',
  STEP_2: '[data-loyalty-step-2]',
  STEP_3: '[data-loyalty-step-3]',
  FIRST_NAME_INPUT: '[data-loyalty-first-name]',
  LAST_NAME_INPUT: '[data-loyalty-last-name]',
  NAME_NEXT_BUTTON: '[data-loyalty-name-next-button]',
  RADIO_INPUT: '[data-loyalty-gender-radio]',
  GENDER_NEXT_BUTTON: '[data-loyalty-gender-next-button]',
  QUESTION_INDICATOR: '[data-loyalty-question-indicator]',
  CHECKBOX_INPUT: '[data-loyalty-consent-checkbox]',
  UNLOCK_BUTTON: '[data-loyalty-unlock-button]',
  REDIRECT_URL: '[data-loyalty-redirect]',
};

class LoyaltyOnboarding extends HTMLElement {
  constructor() {
    super();
    this.viewModel = this.getViewModel();
    this.state = {
      currentStep: 1,
      firstName: '',
      lastName: '',
      selectedGender: null,
      consents: {
        newsletter: false,
        offers: false,
      },
    };
    this._boundHandlers = [];
  }

  connectedCallback() {
    this.attachEvents();
    this.showStep1();
  }

  disconnectedCallback() {
    this._boundHandlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    this._boundHandlers = [];
  }

  getViewModel() {
    return {
      $step1: this.querySelector(LOYALTY_SELECTORS.STEP_1),
      $step2: this.querySelector(LOYALTY_SELECTORS.STEP_2),
      $step3: this.querySelector(LOYALTY_SELECTORS.STEP_3),
      $firstNameInput: this.querySelector(LOYALTY_SELECTORS.FIRST_NAME_INPUT),
      $lastNameInput: this.querySelector(LOYALTY_SELECTORS.LAST_NAME_INPUT),
      $nameNextButton: this.querySelector(LOYALTY_SELECTORS.NAME_NEXT_BUTTON),
      $radioInputs: this.querySelectorAll(LOYALTY_SELECTORS.RADIO_INPUT),
      $genderNextButton: this.querySelector(LOYALTY_SELECTORS.GENDER_NEXT_BUTTON),
      $questionIndicator: this.querySelector(LOYALTY_SELECTORS.QUESTION_INDICATOR),
      $checkboxInputs: this.querySelectorAll(LOYALTY_SELECTORS.CHECKBOX_INPUT),
      $unlockButton: this.querySelector(LOYALTY_SELECTORS.UNLOCK_BUTTON),
      $redirectUrl: this.querySelector(LOYALTY_SELECTORS.REDIRECT_URL),
    };
  }

  attachEvents() {
    // Step 1: Name input events
    const firstNameChange = (e) => {
      this.state.firstName = e.target.value.trim();
      this.updateUI();
    };
    const lastNameChange = (e) => {
      this.state.lastName = e.target.value.trim();
      this.updateUI();
    };
    this.viewModel.$firstNameInput.addEventListener('input', firstNameChange);
    this.viewModel.$lastNameInput.addEventListener('input', lastNameChange);
    this._boundHandlers.push({ el: this.viewModel.$firstNameInput, type: 'input', fn: firstNameChange });
    this._boundHandlers.push({ el: this.viewModel.$lastNameInput, type: 'input', fn: lastNameChange });

    const nameNextClick = () => {
      if (!this.state.firstName || !this.state.lastName) return;
      this.state.currentStep = 2;
      this.showStep2();
    };
    this.viewModel.$nameNextButton.addEventListener('click', nameNextClick);
    this._boundHandlers.push({ el: this.viewModel.$nameNextButton, type: 'click', fn: nameNextClick });

    // Step 2: Gender selection events
    this.viewModel.$radioInputs.forEach((radio) => {
      const fn = (e) => {
        this.state.selectedGender = e.target.dataset.genderValue;
        this.updateUI();
      };
      radio.addEventListener('change', fn);
      this._boundHandlers.push({ el: radio, type: 'change', fn });
    });

    const genderNextClick = () => {
      if (!this.state.selectedGender) return;
      this.state.currentStep = 3;
      this.showStep3();
    };
    this.viewModel.$genderNextButton.addEventListener('click', genderNextClick);
    this._boundHandlers.push({ el: this.viewModel.$genderNextButton, type: 'click', fn: genderNextClick });

    this.viewModel.$checkboxInputs.forEach((checkbox) => {
      const fn = () => {
        const key = checkbox.name;
        this.state.consents[key] = checkbox.checked;
        this.updateUI();
      };
      checkbox.addEventListener('change', fn);
      this._boundHandlers.push({ el: checkbox, type: 'change', fn });
    });

    const unlockClick = () => {
      if (!this.canUnlock()) return;
      this.submitLoyaltyForm();
    };
    this.viewModel.$unlockButton.addEventListener('click', unlockClick);
    this._boundHandlers.push({ el: this.viewModel.$unlockButton, type: 'click', fn: unlockClick });

    this.updateUI();
  }

  showStep1() {
    this.viewModel.$step1.hidden = false;
    this.viewModel.$step2.hidden = true;
    this.viewModel.$step3.hidden = true;
    this.viewModel.$questionIndicator.textContent = this.viewModel.$questionIndicator.getAttribute('data-step-1-copy');
    this.updateUI();
  }

  showStep2() {
    this.viewModel.$step1.hidden = true;
    this.viewModel.$step2.hidden = false;
    this.viewModel.$step3.hidden = true;
    this.viewModel.$questionIndicator.textContent = this.viewModel.$questionIndicator.getAttribute('data-step-2-copy');
    this.updateUI();
  }

  showStep3() {
    this.viewModel.$step1.hidden = true;
    this.viewModel.$step2.hidden = true;
    this.viewModel.$step3.hidden = false;
    this.viewModel.$questionIndicator.textContent = this.viewModel.$questionIndicator.getAttribute('data-step-3-copy');
    this.updateUI();
  }

  updateUI() {
    // Step 1: Name validation
    const hasValidNames = this.state.firstName && this.state.lastName;
    this.viewModel.$nameNextButton.disabled = !hasValidNames;

    // Step 2: Gender validation
    this.viewModel.$genderNextButton.disabled = !this.state.selectedGender;

    // Step 3: Consent validation
    const consentChecked = Object.values(this.state.consents).every(Boolean);
    this.viewModel.$unlockButton.disabled = !consentChecked;
  }

  canUnlock() {
    return Object.values(this.state.consents).every(Boolean);
  }

  async submitLoyaltyForm() {
    const isAldo = String(window.generalProperties.styleguide_brand).includes('aldo');
    const isoCode = window.generalProperties.iso_code;

    const payload = {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      gender: this.state.selectedGender,
      consent: {
        newsletter: this.state.consents.newsletter,
        personalization: this.state.consents.offers,
        source: isAldo ? 'ecomm_crew-signup-page' : 'ecomm_newsletter-signup',
      },
      loyalty: isAldo ? true : false,
      countryCodeV2: isoCode,
    };

    const url = new URL(
      new URLSearchParams(window.location.search).get('return_to') ||
        this.viewModel.$redirectUrl.getAttribute('data-loyalty-redirect-url'),
      window.location.origin
    );

    url.searchParams.set('onboarding_success', 'true');

    this.viewModel.$unlockButton.classList.add('is-loading');

    try {
      const response = await fetch('/apps/headless-controller/customer-onboarding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server responded with ${response.status}: ${text}`);
      }

      console.log('Success:', response);
    } catch (error) {
      console.error('Failed to send customer loyalty data:', error);
    } finally {
      this.checkLoyaltyStatusUntilActive(url.toString());
    }
  }

  checkLoyaltyStatusUntilActive(dashboardUrl, timeout = 10000, interval = 1000) {
    const startTime = Date.now();
    let intervalId;

    const checkLoyaltyStatus = async () => {
      try {
        const response = await fetch(dashboardUrl, { credentials: 'same-origin' });

        if (response.status === 404) {
          clearInterval(intervalId);
          this.viewModel.$unlockButton.classList.remove('is-loading');
          window.location.href = dashboardUrl;
          return false;
        }

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const loyaltyStatus = doc.querySelector('#customer-loyalty-status');

        if (loyaltyStatus?.value === 'ACTIVE') {
          clearInterval(intervalId);
          this.viewModel.$unlockButton.classList.remove('is-loading');
          window.location.href = dashboardUrl;
          return true;
        }
      } catch (error) {
        // Silent error handling
      }

      if (Date.now() - startTime > timeout) {
        clearInterval(intervalId);
        this.viewModel.$unlockButton.classList.remove('is-loading');
        window.location.href = dashboardUrl;
        return false;
      }
    };

    intervalId = setInterval(checkLoyaltyStatus, interval);
    checkLoyaltyStatus();
  }
}

customElements.define('loyalty-onboarding', LoyaltyOnboarding);
